import React, { useEffect, useState, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Panel, Text } from 'shared/components'
import { Link, useHistory } from 'react-router-dom'
import { StoreContext } from 'store/context'
import { setCookie, getCookie } from 'utils/cookie'
import { STORAGE_KEYS } from 'constants/storage.keys'
import links from './links'
import styles from './styles'

const AboutUs = () => {
  const history = useHistory()
  const { auth } = useContext(StoreContext)
  const browserWindow = globalThis.window
  const isBrowser = !!browserWindow
  const [isLoading, setIsLoading] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [currentScreen, setCurrentScreen] = useState(0)
  const [onboardingCompleted, setOnboardingCompleted] = useState(false)
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => {
    // Initialize with current screen size
    return isBrowser && browserWindow.innerWidth <= 1024
  })

  // Onboarding screens data
  const onboardingScreens = [
    {
      backgroundImage: './img/signup-first.png',
      title: 'Never miss a game',
      subtitle: 'Follow all your favorite sports\nin one place',
      showLoginButton: false
    },
    {
      backgroundImage: './img/signup-second.png',
      title: 'InWin for the greater good',
      subtitle: 'InWin to support\ncharities with every game',
      showLoginButton: false
    },
    {
      backgroundImage: './img/signup-third.png',
      title: 'InWin',
      subtitle: 'Donate for every goal,\ntriple-double, touchdown, or\nhomerun!',
      showLoginButton: true
    }
  ]

  // Detect device type
  const checkMobileOrTablet = () => {
    const isMobile = browserWindow.innerWidth <= 1024 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(globalThis.navigator?.userAgent ?? '')

    return isMobile
  }

  // Add resize event listener to update mobile detection
  useEffect(() => {
    if (!isBrowser) {
      return undefined
    }

    const handleResize = () => {
      const isMobile = checkMobileOrTablet()
      setIsMobileOrTablet(isMobile)
    }

    browserWindow.addEventListener('resize', handleResize)

    // Initial check
    handleResize()

    return () => browserWindow.removeEventListener('resize', handleResize)
  }, [isBrowser])

  // Check if onboarding has been completed before
  const checkOnboardingStatus = () => {
    const hasCompletedOnboarding = getCookie(STORAGE_KEYS.ONBOARDING_COMPLETED)
    return hasCompletedOnboarding === 'true'
  }

  // Mark onboarding as completed
  const completeOnboarding = () => {
    setCookie(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true', {
      expires: 365, // 1 year
      path: '/',
      secure: true,
      sameSite: 'Strict'
    })
    setOnboardingCompleted(true)
  }

  useEffect(() => {
    // Check if this is a direct navigation (e.g., from sidebar menu)
    const urlParams = new URLSearchParams(browserWindow?.location?.search ?? '')
    const isDirectNavigation = urlParams.get('direct') === 'true'

    // Check if user is already logged in
    if (auth.isSignedIn) {
      // For logged-in users, check device type
      if (!isMobileOrTablet) {
              // Desktop users - only redirect if NOT a direct navigation
              if (isDirectNavigation) {
                setIsLoading(false)
                return
              }
              history.replace('/main/events')
      }

      // Mobile/Tablet users - show splash screen first, then redirect to app
      const timer = setTimeout(() => {
        history.replace('/main/events')
      }, 3000) // 3 seconds splash

      return () => clearTimeout(timer)
    }

    // For non-logged-in users, check device type
    if (!isMobileOrTablet) {
      // Desktop users - show the AboutUs component, no redirects
      setIsLoading(false)
      return
    }

    // Mobile/Tablet users - check onboarding status
    const hasCompletedOnboarding = checkOnboardingStatus()

    if (hasCompletedOnboarding) {
      // Mobile/Tablet users - show the third screen with login/signup buttons
      setOnboardingCompleted(true)
      setShowOnboarding(true)
      setCurrentScreen(2) // Show the last screen
      setIsLoading(false)
      return
    }

    // Mobile/Tablet users - show splash screen first
    const timer = setTimeout(() => {
      setIsLoading(false)
      setShowOnboarding(true)
    }, 3000) // 3 seconds

    return () => clearTimeout(timer)
  }, [history, auth.isSignedIn, isMobileOrTablet])

  // Auto-advance onboarding screens (forward only) - only for first-time users
  useEffect(() => {
    if (showOnboarding && !onboardingCompleted && currentScreen < onboardingScreens.length - 1) {
      const autoAdvance = setTimeout(() => {
        setCurrentScreen((prev) => prev + 1)
      }, 4000) // 4 seconds per screen

      return () => clearTimeout(autoAdvance)
    }
  }, [showOnboarding, currentScreen, onboardingScreens.length, onboardingCompleted])

  // Mark onboarding as completed when reaching the last screen
  useEffect(() => {
    if (showOnboarding && currentScreen === onboardingScreens.length - 1 && !onboardingCompleted) {
      completeOnboarding()
    }
  }, [showOnboarding, currentScreen, onboardingScreens.length, onboardingCompleted])

  // Show onboarding screen only for mobile/tablet devices
  if (showOnboarding && isMobileOrTablet) {
    const currentScreenData = onboardingScreens[currentScreen]
    const subtitleLines = currentScreenData.subtitle.split('\n')

    const handleBackClick = () => {
      if (currentScreen > 0) {
        setCurrentScreen(currentScreen - 1)
      }
    }

    return (
      <div css={styles.onboardingContainer(currentScreenData.backgroundImage)}>
        {/* SVG clipPath definition for curved background */}
        <svg width="0" height="0">
          <defs>
            <clipPath id="bottom-curve" clipPathUnits="objectBoundingBox">
              <path d="
                M 0 0 
                H 1 
                V 0.92 
                C 0.75 0.85, 0.25 0.85, 0 0.92 
                Z
              " />
            </clipPath>
          </defs>
        </svg>

        {/* Back arrow - only show on second and third pages, and only for first-time users */}
        {currentScreen > 0 && !onboardingCompleted && (
          <button css={styles.backArrow} onClick={handleBackClick}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div css={[
          styles.onboardingContent,
          currentScreen === 2 && styles.thirdPageContent,
          (currentScreen === 0 || currentScreen === 1) && styles.firstAndSecondPageContent
        ]}>
          {currentScreen === 2 ? (
            <div css={[styles.titleWithLogo, styles.thirdPageTitleContainer]}>
              <img
                src="./img/big_logo_dark.png"
                alt="InWin Logo"
                css={styles.titleLogo}
              />
              <h1 css={styles.onboardingTitle}>{currentScreenData.title}</h1>
            </div>
          ) : (
            <h1 css={styles.onboardingTitle}>{currentScreenData.title}</h1>
          )}
          <p css={[styles.onboardingSubtitle, currentScreen === 2 && styles.thirdPageTextDown, currentScreen === 2 && styles.thirdPageSubtitleSpacing]}>
            {subtitleLines.map((line, i) => (
              <span key={`${currentScreenData.title}-${line}`}>{line}{i < subtitleLines.length - 1 && <br />}</span>
            ))}
          </p>

          {/* Pagination dots - only show for first-time users */}
          {!onboardingCompleted && (
            <div css={styles.paginationDots}>
              {onboardingScreens.map((screen, index) => (
                <div
                  key={screen.title}
                  css={[styles.dot, index === currentScreen && styles.activeDot]}
                  onClick={() => setCurrentScreen(index)}
                />
              ))}
            </div>
          )}

          {/* Buttons */}
          {currentScreenData.showLoginButton ? (
            <div css={styles.buttonContainer}>
              <button
                css={styles.signupButton}
                onClick={() => history.push('/auth/signup')}
              >
                Sign up
              </button>
              <button
                css={styles.loginButton}
                onClick={() => history.push('/auth/login')}
              >
                Log in
              </button>
            </div>
          ) : (
            <button
              css={styles.signupButton}
              onClick={() => history.push('/auth/signup')}
            >
              Sign up
            </button>
          )}

          {/* Bottom swipe indicator - only show for first-time users */}
          {!onboardingCompleted && (
            <div css={[styles.swipeIndicator, currentScreen === 2 && styles.thirdPageSwipeIndicator]}></div>
          )}
        </div>
      </div>
    )
  }

  // Fallback for desktop users who somehow have showOnboarding set to true
  if (showOnboarding && !isMobileOrTablet) {
    setIsLoading(false)
    setShowOnboarding(false)
  }

  // Show splash screen only for mobile/tablet devices
  if (isMobileOrTablet && isLoading) {
    return (
      <div css={styles.splashContainer}>
        <div css={styles.splashContent}>
          {/* Logo */}
          <div css={styles.logoContainer}>
            <div css={styles.logoBorder}>
              <img
                css={styles.logo}
                src="./img/big_logo_dark.png"
                alt="InWin Logo"
                loading="lazy"
              />
            </div>
          </div>

          {/* Title */}
          <h1 css={styles.title}>InWin</h1>

          {/* Subtitle */}
          <p css={styles.subtitle}>InWin on sports for a cause</p>
        </div>

        {/* Loading indicator */}
        <div css={styles.loadingContainer}>
          <div css={styles.loadingBar}></div>
        </div>
      </div>
    )
  }

  // Desktop version - show the original AboutUs component
  const isSignedIn = auth?.isSignedIn

  return (
    <Panel css={styles.container}>
      <Text size={18} bold white>About Us</Text>
      {!isSignedIn && (
        <div css={styles.top}>
          <img
            css={styles.topImage}
            src="./img/aboutUs/aboutUsTop.jpeg"
            alt="about"
            loading="lazy"
          />
          <h3 css={styles.topTitle}>Never miss a game</h3>
          <p css={styles.description}>
            Become an InWin member to see all the tournaments and <br /> be able
            to cheer for your favorite team!
          </p>
          <Button
            css={styles.signUpBtn}
            text="Sign Up"
            onClick={() => history.push('/auth/signup')}
          />
          <span css={styles.logInText}>
            Already have an account?{' '}
            <Link css={styles.logInLink} to="/auth/login">
              Log in
            </Link>
          </span>
        </div>
      )}

      <div css={styles.links}>
        <Link css={styles.link} to={'main/privacy-policy'}>
          <Text size={11}>Privacy Policy</Text>
          <FontAwesomeIcon icon="caret-right" />
        </Link>
          {links.map(({ id, text, link }) => (
            <a
              css={styles.link}
              key={id}
              href={link}
              target="_blank"
              rel="noreferrer"
            >
            <Text size={11}>{text}</Text>
            <FontAwesomeIcon icon="caret-right" />
          </a>
        ))}
      </div>
    </Panel>
  )
}

export default AboutUs
